import { MoreHorizontal, Edit, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface RowActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  /** Reason why delete is disabled. Rendered as a visible sub-line on the
   *  disabled item — a native `title` never fires, because Radix sets
   *  `pointer-events: none` on disabled menu items. */
  deleteTooltip?: string;
  /** Row identifier (e.g. a name or code), used to give each row's trigger a
   *  distinct accessible name instead of N identical "Open menu" buttons. */
  label?: string;
}

export function RowActions({ onEdit, onDelete, deleteDisabled, deleteTooltip, label }: RowActionsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 focus-visible:ring-1 focus-visible:ring-ring">
          <span className="sr-only">{label ? `Open menu for ${label}` : "Open menu"}</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        {onEdit && (
          <DropdownMenuItem onClick={onEdit} className="text-sm font-medium text-slate-700 cursor-pointer">
            <Edit className="mr-2 h-4 w-4 text-slate-500" />
            Edit
          </DropdownMenuItem>
        )}
        
        {onEdit && onDelete && <DropdownMenuSeparator />}
        
        {onDelete && (
          <DropdownMenuItem
            onClick={onDelete}
            disabled={deleteDisabled}
            className={`text-sm font-medium cursor-pointer ${
              deleteDisabled
                ? "text-slate-400"
                : "text-red-600 focus:text-red-700 focus:bg-red-50"
            }`}
          >
            <Trash className={`mr-2 h-4 w-4 ${deleteDisabled ? "text-slate-400" : "text-red-600"}`} />
            <span className="flex flex-col items-start">
              <span>Delete</span>
              {deleteDisabled && deleteTooltip && (
                <span className="text-xs font-normal text-slate-400">{deleteTooltip}</span>
              )}
            </span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
